import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from './auth';
import { useExperiment } from './experiment';

const STORAGE_KEY = 'iamhero_cart';
// Pay-first cart. In the pay-first experiment variant a visitor adds book
// "slots" — a template + chosen variant, with NO story yet — and pays for them
// all at once. The actual stories are created afterwards from the paid order.
// Kept entirely in localStorage (guests AND logged-in users) until checkout,
// where a single storyless multi-item order is created. Separate from the normal
// per-story cart above so the two variants never interfere.
const SLOTS_KEY = 'iamhero_payfirst_cart';
const CartContext = createContext(null);

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch { return []; }
}

function loadSlots() {
  try {
    return JSON.parse(localStorage.getItem(SLOTS_KEY)) || [];
  } catch { return []; }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const { payFirst } = useExperiment();
  // Guest cart (localStorage). Logged-in users use server-side pending orders
  // as their cart instead — see pendingOrders below.
  const [items, setItems] = useState(loadCart);
  const [pendingOrders, setPendingOrders] = useState([]);
  // Pay-first slots (localStorage) — see SLOTS_KEY above.
  const [slots, setSlots] = useState(loadSlots);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(SLOTS_KEY, JSON.stringify(slots));
  }, [slots]);

  const addItem = useCallback((item) => {
    setItems((prev) => [...prev, { ...item, id: Date.now() }]);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  // Persist a guest's in-progress purchase so it survives navigation, payment
  // abandonment, and reload. Keyed by shareToken so retrying the same story
  // updates the existing line instead of duplicating it. (Logged-in users use
  // server-side pending orders as their cart instead — see pendingOrders.)
  const saveGuestItem = useCallback((item) => {
    if (!item || !item.shareToken) return;
    setItems((prev) => {
      const rest = prev.filter((i) => i.shareToken !== item.shareToken);
      return [...rest, { ...item, id: item.shareToken }];
    });
  }, []);

  const removeGuestItemByShareToken = useCallback((shareToken) => {
    if (!shareToken) return;
    setItems((prev) => prev.filter((i) => i.shareToken !== shareToken));
  }, []);

  // ── Pay-first slots ──
  // Add a book slot (template + chosen variant, no story yet). Each add is a
  // distinct line — a parent buying two copies of the same book adds it twice —
  // so slots carry their own generated id rather than deduping by template.
  const addSlot = useCallback((slot) => {
    if (!slot || !slot.templateSlug || !slot.variantSku) return;
    const id = (window.crypto?.randomUUID?.() || `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setSlots((prev) => [...prev, { ...slot, id }]);
  }, []);

  const removeSlot = useCallback((id) => {
    setSlots((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const clearSlots = useCallback(() => setSlots([]), []);

  // The cart, for a logged-in user, is their unpaid (pending_payment) orders —
  // the same records shown in "My Orders". Generating a story auto-creates one;
  // paying for it flips its status so it drops off here.
  const refreshPendingOrders = useCallback(async () => {
    if (!user) { setPendingOrders([]); return; }
    try {
      const orders = await api.get('/api/orders');
      setPendingOrders((orders || []).filter((o) => o.status === 'pending_payment'));
    } catch {
      setPendingOrders([]);
    }
  }, [user]);

  useEffect(() => { refreshPendingOrders(); }, [refreshPendingOrders]);

  // Re-fetch when the tab regains focus or becomes visible again — e.g. on
  // returning from Stripe Checkout — so the cart/badge repopulate immediately
  // instead of briefly showing a stale empty state.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') refreshPendingOrders();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshPendingOrders]);

  // The header badge reflects the ACTIVE cart for this visitor's variant: the
  // pay-first slots in the pay-first variant, otherwise the normal per-story cart
  // (server pending orders for logged-in users, the guest localStorage cart else).
  const count = payFirst ? slots.length : (user ? pendingOrders.length : items.length);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, clearCart, saveGuestItem, removeGuestItemByShareToken,
      slots, addSlot, removeSlot, clearSlots,
      count, pendingOrders, refreshPendingOrders,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
